import { RotateCwIcon } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import modalStore from '@client/store/modalStore';
import webViewStore from '@client/store/webViewStore';
import { twMerge } from 'tailwind-merge';

const THRESHOLD = 100;

const PullToRefresh = observer(() => {
    const [height, setHeight] = useState(0);

    useEffect(() => {
        if (modalStore.isModalOpen) {
            return;
        }

        let canPullToRefresh = false;
        let startY = 0;
        let startTime = 0;
        let delta = 0;

        const handleTouchStart = (e: TouchEvent) => {
            // 페이지 맨 위에서 시작해야 함
            if (window.scrollY > 0) {
                return;
            }

            if ((e.target as HTMLElement)?.closest('[data-ignore-pull-to-refresh]')) {
                return;
            }

            startY = e.touches[0].clientY;
            canPullToRefresh = true;
            startTime = Date.now();
        };

        const handleTouchMove = (e: TouchEvent) => {
            if (!canPullToRefresh) {
                return;
            }

            delta = e.touches[0].clientY - startY;

            if (window.scrollY <= 0 && delta > 0) {
                e.preventDefault();
                setHeight(delta / 2);
            }
        };

        const handleTouchEnd = async () => {
            if (!canPullToRefresh) {
                return;
            }

            if (delta > THRESHOLD && Date.now() - startTime > 500) {
                // 새로고침 하는 동안 높이 고정
                setHeight(THRESHOLD);

                // 리로드 가능할 때 햅틱 효과
                await webViewStore.sendMessageToApp('haptic', {
                    style: 'light',
                });

                location.reload();
            } else {
                setHeight(0);
            }

            canPullToRefresh = false;
            startY = 0;
            startTime = 0;
            delta = 0;
        };

        document.addEventListener('touchstart', handleTouchStart, { passive: true });
        document.addEventListener('touchmove', handleTouchMove, { passive: false });
        document.addEventListener('touchend', handleTouchEnd);

        return () => {
            document.removeEventListener('touchstart', handleTouchStart);
            document.removeEventListener('touchmove', handleTouchMove);
            document.removeEventListener('touchend', handleTouchEnd);
        };
    }, [modalStore.isModalOpen]);

    if (!webViewStore.isWebView) {
        return null;
    }

    const ratio = Math.min(height / THRESHOLD, 1);
    // 아이콘 바꾸긴 번거로우니 현재 아이콘을 유지하면서 회전 각도를 변경해서 자연스럽게 보이게 해둠
    // 높이에 따라 화살표가 채워지는 것처럶 보이게 맞춘 각도
    // THRESHOLD 값을 변경하면 이 값도 변경해줘야 자연스러움
    const rotateDeg = -105 + ratio * 360 * 1.5;

    return (
        <div
            className="relative overflow-hidden"
            style={{
                height: `${height}px`,
                opacity: ratio,
            }}
        >
            <RotateCwIcon
                className={twMerge(
                    'text-primary absolute top-5 left-1/2 mx-auto size-6 -translate-x-1/2',
                    ratio === 1 && 'animate-spin',
                )}
                style={{
                    top: `${5 + 20 * ratio}px`,
                    rotate: `${rotateDeg}deg`,
                }}
            />
        </div>
    );
});

export default PullToRefresh;
